import { CORE_API_BASE_URL } from '@chk/env-variables';

import { addSentryAttachment, captureException } from '../sentry/sentry';
import { sanitizeEncodedJSONByDotNotation } from '../sentry/sentry.utils';

export class ErrorWithDetails<T = unknown> extends Error {
  status: number;
  data: T;

  constructor(message: string, status: number, data: T) {
    super(message);
    this.status = status;
    this.data = data;
  }
}

export async function asyncFetch(
  url: string,
  init: RequestInit,
  apiBaseUrl: string = CORE_API_BASE_URL,
  shouldTrackException = true
) {
  const fullUrl = `${apiBaseUrl}${url}`;
  const requestId = getRequestId();
  const requestMethod = init.method ?? 'GET';

  trackRequestInSentry(requestId, requestMethod, fullUrl, init);

  try {
    const response = await fetch(`${fullUrl}`, init);

    await trackResponseInSentry(response, requestId, requestMethod, fullUrl);

    const data = await assertSuccessfulResponse(response);

    return data;
  } catch (error) {
    // NOTE: needed for backward compatibility.
    // Ideally, all requests just return the error and it would be handled (or tracked) locally in the context/view/component
    // instead of in the main util
    if (shouldTrackException && error instanceof ErrorWithDetails) {
      captureException(error);
    }

    return Promise.reject(error);
  }
}

function trackRequestInSentry(
  requestId: string,
  requestMethod: string,
  fullUrl: string,
  init: RequestInit
) {
  const data = init.body?.toString() ?? '{}';

  addSentryAttachment({
    filename: `#${requestId} request: ${requestMethod} ${fullUrl}.json`,
    data: sanitizeEncodedJSONByDotNotation(data, [
      'sca.username',
      'sca.password',
    ]),
  });
}

async function trackResponseInSentry(
  response: Response,
  requestId: string,
  requestMethod: string,
  fullUrl: string
) {
  const isResponseMimeJson = response.headers
    .get('content-type')
    ?.includes('json');

  addSentryAttachment({
    filename: `#${requestId} response ${response.status}: ${requestMethod} ${fullUrl}.${isResponseMimeJson ? 'json' : 'txt'}`,
    data: await response.clone().text(),
  });
}

async function parseJsonFromResponse(response: Response) {
  const data = await response.text();

  if (!data) {
    return null;
  }

  try {
    return JSON.parse(data);
  } catch {
    throw new ErrorWithDetails('Invalid JSON response', response.status, {
      data,
    });
  }
}

export async function assertSuccessfulResponse(response: Response) {
  const data = await parseJsonFromResponse(response);

  if (!response.ok) {
    throw new ErrorWithDetails(
      `${response.status} ${response.statusText}`,
      response.status,
      data
    );
  }

  return data;
}

const toHex = (buf: Uint8Array) => {
  return Array.from(buf, (byte) => byte.toString(16).padStart(2, '0')).join('');
};

const getRequestId = () => {
  const crypto = globalThis.crypto as Crypto | undefined;

  if (crypto?.randomUUID) {
    return crypto.randomUUID().split('-')[0];
  }

  if (crypto?.getRandomValues) {
    const buf = new Uint8Array(4);
    crypto.getRandomValues(buf);
    return toHex(buf);
  }

  return (Date.now().toString(36) + Math.random().toString(36)).slice(-8);
};
